import React, { useEffect } from "react"
import { useState } from "react"
import { Dropdown, Modal } from "react-bootstrap"
import { Button } from "react-bootstrap"
import { Form } from "react-bootstrap"
import { Accordion } from "react-bootstrap"
import { EllipsisVertical } from "lucide-react"
import { VesselTrackingList } from "../hooks/useFetchList"
import { useFetchVesselsByList } from "../hooks/useFetchVesselsByList"
import { useAddList } from "../hooks/useAddList"
import { AddList } from "../hooks/useAddList"
import { toast } from "react-toastify"
import VesselDeleteListModal from "./VesselDeleteListModal"
import VesselTrackingEditListModal from "./VesselTrackingEditListModal"

interface VesselTrackingCreateListModalProps {
  showListModal: boolean
  closeListModal: () => void
  vesselList: VesselTrackingList[]
  fetchVesselTrackingList: () => Promise<void>
  onListCreated?: (list: VesselTrackingList) => void
  activeList: VesselTrackingList
  setActiveList: React.Dispatch<
      React.SetStateAction<VesselTrackingList | undefined>
    >
}

function VesselTrackingCreateListModal({
  showListModal,
  closeListModal,
  vesselList,
  fetchVesselTrackingList,
  onListCreated,
  activeList,
  setActiveList
}: VesselTrackingCreateListModalProps) {
  const { getVesselsByList, vesselInfoByList } = useFetchVesselsByList()
  const [prevetList, setPrevetList] = useState<VesselTrackingList[]>(
    vesselList.filter((list) => list.listType === "prevet")
  )
  const [userList, setUserList] = useState<VesselTrackingList[]>(
    vesselList.filter((list) => list.listType === "user")
  )
  
  const { createList, isAddListLoading } = useAddList()
  const [addListName, setAddListName] = useState<string>("")
  useEffect(() => {
    getVesselsByList(vesselList.map((list) => list.id))
  }, [])
  const [deleteId, setDeleteId] = useState<string>("")
  const [showEditListModal, setShowEditListModal] = useState<boolean>(false)
  const [editListId, setEditListId] = useState<string>("")
  const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false)

  useEffect(() => {
    setPrevetList(vesselList.filter((list) => list.listType === "prevet"))
    setUserList(vesselList.filter((list) => list.listType === "user"))
  }, [vesselList])

  const handleAddList = async (listName: string) => {
    if (addListName.trim() === "") {
      const message = "List name cannot be empty"
      console.warn(message)
      toast.warn(message, {
        position: "bottom-right",
        autoClose: 8000,
      })
      return
    }
    if (
      vesselList
        .filter((list) => list.listType === "user")
        .some((list) => list.listName === addListName)
    ) {
      const message = "List with this name already exists"
      toast.warn(message, {
        position: "bottom-right",
        autoClose: 8000,
      })
    } else {
      const addListObj: AddList = {
        orgId: null,
        listName: addListName,
        userId: null,
        listType: "user",
      }
      try {
        const newList = await createList(addListObj)
        toast.success("List created successfully", {
          position: "bottom-right",
          autoClose: 8000,
        })
        setAddListName("")
        if (newList && onListCreated) {
          onListCreated(newList)
        } else {
          // fallback: server didn't echo the new list, sync from scratch
          fetchVesselTrackingList()
        }
      } catch (err: unknown) {
        const message = "Unable to create list"
        console.error(message, err)
      }
    }
  }

  return (
    <>
      {/* delete list modal */}
      <VesselDeleteListModal
        listID={deleteId}
        showDelete={showDeleteModal}
        setShowDelete={setShowDeleteModal}
        fetchVesselTrackingList={fetchVesselTrackingList}
        activeListId = {activeList.id}
        setActiveList = {setActiveList}
      />
      {/* Edit list modal */}
      <VesselTrackingEditListModal
        listID={editListId}
        showEdit={showEditListModal}
        setShowEdit={setShowEditListModal}
        fetchVesselTrackingList={fetchVesselTrackingList}
      />

      <Modal show={showListModal} onHide={closeListModal} size="lg">
        <Modal.Header
          className="d-flex flex-row gap-3 align-items-end"
          closeButton
        >
          <h5 className="text-nowrap">Create List</h5>
          <Form.Control
            type="text"
            placeholder="Enter list name"
            value={addListName}
            onChange={(e) => setAddListName(e.target.value)}
          />
          <Button
            variant="primary text-white"
            disabled={isAddListLoading}
            onClick={() => handleAddList(addListName)}
             className="fs-3 px-3 py-0"
          >
            {isAddListLoading ? (
              <span
                className="spinner-border spinner-border-sm"
                role="status"
                aria-hidden="true"
              ></span>
            ) : (
              "+"
            )}
          </Button>
        </Modal.Header>
        <Modal.Body>
          <Accordion defaultActiveKey="null">
            <span style={{ fontWeight: "bold", fontSize: "1rem" }}>
              Prevetting
            </span>
            {prevetList.length !== 0 ? (
              prevetList.map((list) => (
                <Accordion.Item eventKey={list.id} key={list.id}>
                  <Accordion.Header>
                    <div className="d-flex justify-content-between align-items-center w-100 pe-4 ps-4">
                      <span>{list.listName}</span>
                      <div onClick={(e) => e.stopPropagation()}></div>
                    </div>
                  </Accordion.Header>
                  <Accordion.Body>
                    <table className="table table-bordered">
                      <thead>
                        <tr>
                          <th style={{ fontWeight: "bold" }}>IMO</th>
                          <th style={{ fontWeight: "bold" }}>Vessel Name</th>
                          <th style={{ fontWeight: "bold" }}>DWT</th>
                          <th style={{ fontWeight: "bold" }}>Segment</th>
                          <th style={{ fontWeight: "bold" }}>
                            Expiration Date
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {vesselInfoByList
                          .find((vessel) => vessel.listID === list.id)
                          ?.vesselInfo.map((vessel) => (
                            <tr key={vessel.id}>
                              <td>{vessel.vesselImo}</td>
                              <td>{vessel.vesselName}</td>
                              <td>{vessel.vesselDwt}</td>
                              <td>{vessel.vesselSegment}</td>
                              <td>
                                <div className="bg-black text-white py-1 ps-2 border-radius-1 rounded">
                                  {new Date(vessel.expirationDate)
                                    .toLocaleDateString("en-US", {
                                      year: "numeric",
                                      day: "numeric",
                                      month: "short",
                                    })
                                    .toUpperCase()}
                                </div>
                              </td>
                            </tr>
                          ))}
                      </tbody>
                    </table>
                  </Accordion.Body>
                </Accordion.Item>
              ))
            ) : (
              <div className="text-center text-muted my-2">
                No list available
              </div>
            )}

            {/* user list */}
            <span style={{ fontWeight: "bold", fontSize: "1rem" }}>
              User's Favorite
            </span>
            {userList.length !== 0 ? (
              userList.map((list) => (
                <Accordion.Item eventKey={list.id} key={list.id}>
                  <Accordion.Header>
                    <div className="d-flex justify-content-between align-items-center w-100 pe-4 ps-4">
                      <span>{list.listName}</span>
                      <div onClick={(e) => e.stopPropagation()}>
                        <Dropdown>
                          <Dropdown.Toggle
                            className="sortable-item-button sortable-button-common"
                            id="menu-options"
                          >
                            <EllipsisVertical className="options-icon" />
                          </Dropdown.Toggle>
                          <Dropdown.Menu>
                            <Dropdown.Item
                              onClick={() => {
                                setShowEditListModal(true)
                                setEditListId(list.id)
                              }}
                            >
                              Edit list name
                            </Dropdown.Item>
                            <Dropdown.Item
                              onClick={() => {
                                setDeleteId(list.id)
                                setShowDeleteModal(true)
                              }}
                            >
                              Delete list
                            </Dropdown.Item>
                          </Dropdown.Menu>
                        </Dropdown>
                      </div>
                    </div>
                  </Accordion.Header>
                  <Accordion.Body>
                    <table className="table table-bordered">
                      <thead>
                        <tr>
                          <th style={{ fontWeight: "bold" }}>IMO</th>
                          <th style={{ fontWeight: "bold" }}>Vessel Name</th>
                          <th style={{ fontWeight: "bold" }}>DWT</th>
                          <th style={{ fontWeight: "bold" }}>Segment</th>
                          <th style={{ fontWeight: "bold" }}>
                            Expiration Date
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {vesselInfoByList
                          .find((vessel) => vessel.listID === list.id)
                          ?.vesselInfo.map((vessel) => (
                            <tr key={vessel.id}>
                              <td>{vessel.vesselImo}</td>
                              <td>{vessel.vesselName}</td>
                              <td>{vessel.vesselDwt}</td>
                              <td>{vessel.vesselSegment}</td>
                              <td>
                                <div className="bg-black text-white py-1 ps-2 border-radius-1 rounded">
                                  {new Date(vessel.expirationDate)
                                    .toLocaleDateString("en-US", {
                                      year: "numeric",
                                      day: "numeric",
                                      month: "short",
                                    })
                                    .toUpperCase()}
                                </div>
                              </td>
                            </tr>
                          ))}
                      </tbody>
                    </table>
                  </Accordion.Body>
                </Accordion.Item>
              ))
            ) : (
              <div className="text-center text-muted my-2">
                No list available
              </div>
            )}
          </Accordion>
        </Modal.Body>
      </Modal>
    </>
  )
}

export default VesselTrackingCreateListModal
